import { CreateDtrLocationBulkInput, CreateDtrLocationInput, DtrLocation } from '@/models/gen/graphql';

import { CreateDtrLocationBulkDocument } from '@/api/queries';
import { GraphApiResponse } from '@/api/core';
import { Validation } from '@/utils/validations';
import graphApi from '@/api';
import { handleError } from '@/utils/custom';

type CreateDtrLocationBulkGraphApiResponse = GraphApiResponse<typeof CreateDtrLocationBulkDocument>;

const title = 'Create DTR Locations';

export const createDtrLocationValidator = new Validation.Validator({
  'name!': (val: string): Validation.Validity => ({
    valid: val?.length >= 2,
    message: val?.length >= 2 ? 'DtrLocation name must be at least 2 characters.' : undefined,
  }),
  'address!': (val: string): Validation.Validity => ({
    valid: val?.length >= 2,
    message: val?.length >= 2 ? 'Please enter a valid address.' : undefined,
  }),
  'airportCode!': (val: string): Validation.Validity => ({
    valid: !!val?.length,
  }),
  coordinates: (val: { lat: string; lng: string }): Validation.Validity => ({
    valid:
      Validation.isNumber(parseInt(val?.lat)) && Validation.isNumber(parseInt(val?.lng))
        ? true
        : !!val?.lat || !!val?.lng
          ? false
          : undefined,
    defaultValue: { lat: 0.0, lng: 0.0 },
  }),
  cityName: (val: string): Validation.Validity => ({
    valid: !!val && Validation.isAlphanumeric(val) ? true : val?.length ? false : undefined,
  }),
  stateCode: (val: string): Validation.Validity => ({
    valid: !!val && Validation.isAlphanumeric(val) ? true : val?.length ? false : undefined,
  }),
  zipCode: (val: string): Validation.Validity => ({
    valid: !!val && Validation.isAlphanumeric(val) ? true : val?.length ? false : undefined,
  }),
  countryCode: (val: string): Validation.Validity => ({
    valid: !!val && new RegExp('^[A-Z]{2,3}').test(val) ? true : val?.length ? false : undefined,
    defaultValue: 'USA',
  }),
});

const createDtrLocationBulkResponseSelector = (res: CreateDtrLocationBulkGraphApiResponse): DtrLocation[] =>
  !res.errors ? ((res?.createDtrLocationBulk?.output || []).map(({ node }) => node) as DtrLocation[]) : undefined;

const [runCreateDtrLocationBulk] = graphApi(CreateDtrLocationBulkDocument, {
  onError: (res: CreateDtrLocationBulkGraphApiResponse): void => handleError(res, { notification: { title } }),
});

const createDtrLocationBulk = async (dtrLocations: CreateDtrLocationInput[]): Promise<DtrLocation[]> => {
  const values: CreateDtrLocationInput[] = dtrLocations.map(
    (dtrLocation: CreateDtrLocationInput): CreateDtrLocationInput => createDtrLocationValidator.create(dtrLocation)
  );

  const input: CreateDtrLocationBulkInput = {
    values,
  };
  const res = await runCreateDtrLocationBulk({ input });
  const data = createDtrLocationBulkResponseSelector(res);

  return data;
};

export default createDtrLocationBulk;
